import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Plus, Mic2, Pencil, Trash2, X, Link2, ExternalLink, Bell } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import ImageUpload from "../../components/ImageUpload";

const emptyAct = { name: "", slug: "", bio: "", image_url: "" };

export default function ActsTab() {
  const [acts, setActs] = useState(null);
  const [bizSlug, setBizSlug] = useState("");
  const [modal, setModal] = useState(null); // act being created/edited
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/business/acts").then(({ data }) => setActs(data || [])).catch(() => setActs([]));
  useEffect(() => {
    load();
    api.get("/business/me").then(({ data }) => setBizSlug(data.slug || "")).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!modal.name.trim()) return;
    setBusy(true);
    try {
      const payload = { name: modal.name.trim(), slug: modal.slug.trim(), bio: modal.bio.trim(), image_url: modal.image_url || "" };
      if (modal.id) await api.put(`/business/acts/${modal.id}`, payload);
      else await api.post("/business/acts", payload);
      toast({ title: "Saved", description: "Artist saved to your database." });
      setModal(null); load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not save artist", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? It will be removed from any events it's on.`)) return;
    await api.delete(`/business/acts/${a.id}`);
    toast({ title: "Deleted" }); load();
  };

  const copyLink = (a) => {
    const url = `${window.location.origin}/acts/${a.slug}`;
    navigator.clipboard?.writeText(url);
    toast({ title: "Link copied", description: url });
  };

  if (!acts) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  return (
    <div className="max-w-[900px]">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-[#8a97a8] max-w-lg">Build a database of the artists/acts you regularly book. Add them to events and share a link that lists everything an act is playing.</p>
        <button onClick={() => setModal({ ...emptyAct })} data-testid="add-act-btn" className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 whitespace-nowrap"><Plus size={16} /> Add artist</button>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden">
        {acts.length === 0 ? (
          <p className="p-8 text-sm text-[#8a97a8] text-center">No artists yet. Add your first act to get started.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Artist", "Public link", "Followers", "Events", "Upcoming", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {acts.map((a) => (
                <tr key={a.id} className="border-t border-gray-50" data-testid={`act-row-${a.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-lg overflow-hidden bg-[#f0f3f8] flex items-center justify-center text-[#8b5cf6] shrink-0">
                        {a.image_url ? <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" /> : <Mic2 size={16} />}
                      </span>
                      <span className="font-medium text-[#16243b]">{a.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-[#5a6b80]">/acts/{a.slug}</code>
                      <button onClick={() => copyLink(a)} data-testid={`act-copy-${a.id}`} title="Copy link" className="text-[#8a97a8] hover:text-[#2ec9c9]"><Link2 size={14} /></button>
                      {bizSlug && <a href={`/store/${bizSlug}/acts/${a.slug}`} target="_blank" rel="noreferrer" title="Open store page" className="text-[#8a97a8] hover:text-[#2ec9c9]"><ExternalLink size={14} /></a>}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5 text-[#5a6b80]" data-testid={`act-followers-${a.id}`}>
                      <Bell size={13} className="text-[#8b5cf6]" /> <span className="font-medium text-[#16243b]">{a.followers || 0}</span>
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[#5a6b80]">{a.events || 0}</td>
                  <td className="px-5 py-3 text-[#5a6b80]">{a.upcoming || 0}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setModal({ id: a.id, name: a.name, slug: a.slug, bio: a.bio || "", image_url: a.image_url || "" })} data-testid={`edit-act-${a.id}`} className="text-[#5a6b80] hover:text-[#16243b] mr-3 inline-flex items-center gap-1"><Pencil size={14} /> Edit</button>
                    <button onClick={() => del(a)} data-testid={`del-act-${a.id}`} className="text-red-500 hover:text-red-600 inline-flex items-center gap-1"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="act-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#16243b] text-lg">{modal.id ? "Edit artist" : "Add artist"}</h3>
              <button onClick={() => setModal(null)} className="text-[#8a97a8] hover:text-[#16243b]"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Artist / act name</label>
                <input value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} data-testid="act-name"
                  placeholder="e.g. The Killers" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              {modal.id && (
                <div>
                  <label className="block text-sm font-medium text-[#16243b] mb-1.5">Link slug</label>
                  <div className="flex items-center rounded-xl border border-gray-200 focus-within:border-[#2ec9c9] overflow-hidden">
                    <span className="px-3 py-2.5 text-sm text-[#8a97a8] bg-[#f6f8fb] whitespace-nowrap">/acts/</span>
                    <input value={modal.slug} onChange={(e) => setModal({ ...modal, slug: e.target.value })} data-testid="act-slug"
                      placeholder="the-killers" className="flex-1 px-3 py-2.5 outline-none" />
                  </div>
                  <p className="text-xs text-[#8a97a8] mt-1">You can customise the public link. It'll be tidied and made unique automatically.</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Short bio <span className="text-[#8a97a8] font-normal">(optional)</span></label>
                <textarea value={modal.bio} onChange={(e) => setModal({ ...modal, bio: e.target.value })} data-testid="act-bio" rows={3}
                  placeholder="A little about this artist…" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              <ImageUpload label="Artist image (optional)" value={modal.image_url} onChange={(v) => setModal({ ...modal, image_url: v })} testid="act-image" />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModal(null)} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">Cancel</button>
                <button disabled={busy} data-testid="save-act-btn" className="gp-btn-teal rounded-xl px-6 py-2.5 text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2">
                  {busy ? <Loader2 className="animate-spin" size={16} /> : null} Save artist
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
